import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import i18n from '@/i18n';

/**
 * "Sessions & devices" data for the Privacy settings tab.
 *
 * Backed by /api/me/sessions (list / revoke one / revoke others). Revoking
 * is server-authoritative; we refetch the list after each change rather than
 * mutating cache optimistically, since a revoke can race with the touch that
 * updates lastSeenAt.
 */

export interface SessionInfo {
  id: string;
  current: boolean;
  createdAt: string;
  lastSeenAt: string;
  uaHint: string | null;
}

export const SESSIONS_QUERY_KEY = ['me', 'sessions'] as const;

export interface UseSessions {
  sessions: SessionInfo[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** Revoke a single (non-current) session. */
  revoke: (id: string) => void;
  /** The id currently being revoked (for per-row spinners), else null. */
  revokingId: string | null;
  /** Log out every other device, keeping this one. */
  revokeOthers: () => void;
  revokingOthers: boolean;
  /** True when there is at least one other device to log out. */
  hasOthers: boolean;
}

export function useSessions(): UseSessions {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: () => api.listSessions(),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });
  };

  const revokeOne = useMutation({
    mutationFn: (id: string) => api.revokeSession(id),
    onSuccess: invalidate,
    onError: (err) => {
      toast.error(...errToToast(err, i18n.t('lib2.sessions.revokeOneFailed')));
    },
  });

  const revokeOthers = useMutation({
    mutationFn: () => api.revokeOtherSessions(),
    onSuccess: (res) => {
      invalidate();
      toast.success(
        res.revoked > 0
          ? i18n.t('lib2.sessions.revokedCount', { count: res.revoked })
          : i18n.t('lib2.sessions.noOthers'),
      );
    },
    onError: (err) => {
      toast.error(...errToToast(err, i18n.t('lib2.sessions.revokeOthersFailed')));
    },
  });

  const sessions = query.data?.sessions ?? [];

  return {
    sessions,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
    revoke: (id: string) => revokeOne.mutate(id),
    revokingId: revokeOne.isPending ? (revokeOne.variables ?? null) : null,
    revokeOthers: () => revokeOthers.mutate(),
    revokingOthers: revokeOthers.isPending,
    hasOthers: sessions.some((s) => !s.current),
  };
}
